import React, { useEffect, useRef, useState } from 'react';

const CARDS = [
  {
    id: 'mission',
    title: 'Our Mission',
    text:
      'To empower every Indian with seamless access to verified properties, transparent transactions, and expert guidance, making real estate dreams achievable for everyone.',
    highlights: ['Verified access', 'Transparent deals', 'Expert guidance'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    id: 'vision',
    title: 'Our Vision',
    text:
      "To become India's most trusted and innovative real estate platform, transforming how people buy, sell, and rent properties through technology, transparency, and exceptional service.",
    highlights: ['Trusted platform', 'Smart technology', 'Exceptional service'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
  },
];

const MissionVisionSection = () => {
  const sectionRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) {
      setIsActive(true);
      return undefined;
    }

    let done = false;
    const activate = () => {
      if (done) return;
      done = true;
      setIsActive(true);
    };

    const isInView = () => {
      const rect = section.getBoundingClientRect();
      return rect.top < window.innerHeight * 0.88 && rect.bottom > 0;
    };

    if (isInView()) {
      activate();
      return undefined;
    }

    const fallback = window.setTimeout(activate, 700);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) activate();
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(section);

    const onScroll = () => {
      if (isInView()) {
        activate();
        observer.disconnect();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`about-section about-mission-vision about-mission-vision-section${isActive ? ' is-active' : ''}`}
      aria-labelledby="mission-vision-heading"
    >
      <div className="mv-ambient mv-ambient--one" aria-hidden="true" />
      <div className="mv-ambient mv-ambient--two" aria-hidden="true" />

      <div className="about-container">
        <header className="mv-page-header about-section-header">
          <h2 id="mission-vision-heading" className="about-section-title story-reveal">
            Mission &amp; Vision
          </h2>
          <div className="about-title-underline" aria-hidden="true" />
          <p className="about-section-subtitle story-reveal story-reveal-delay-1">
            What drives us today — and the future we are building for real estate in India.
          </p>
        </header>

        <div className="mission-vision-grid">
          {CARDS.map((card, index) => (
            <article
              key={card.id}
              className={`mission-vision-card mission-vision-card--${card.id} story-reveal story-reveal-delay-${
                index + 2
              }`}
            >
              <div className="mv-card-accent" aria-hidden="true" />

              <div className="mission-vision-icon">{card.icon}</div>

              <h3 className="mission-vision-title">{card.title}</h3>
              <p className="mission-vision-text">{card.text}</p>

              <ul className="mv-highlights">
                {card.highlights.map((item) => (
                  <li key={item} className="mv-highlight-item">
                    <span className="mv-highlight-dot" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MissionVisionSection;
